/**
 * RealtimeClient — wraps the WebSocket session with OpenAI's
 * gpt-4o-realtime-preview, via our /api/realtime proxy.
 *
 * The client owns:
 *   - the mic capture pipeline (getUserMedia → AudioWorklet →
 *     base64 PCM16 chunks → ws),
 *   - the output audio scheduler (base64 PCM16 deltas →
 *     AudioBuffer → AudioBufferSourceNode, scheduled tail-to-head
 *     for gapless playback),
 *   - barge-in: when the server VAD fires speech_started while the
 *     assistant is talking, we stop the in-flight audio sources
 *     AND tell the server to cancel its response.
 *
 * Callers wire up event callbacks for the chat UI:
 *   onUserTranscript(text)              — final user transcript
 *   onAssistantTranscriptDelta(delta)   — streaming assistant text
 *   onAssistantTranscriptDone(text)     — final assistant text
 *   onSpeechStart() / onSpeechStop()    — VAD events
 *   onError(err)                        — anything went wrong
 *   onClose()                           — session ended
 */
export class RealtimeClient {
  constructor(opts = {}) {
    this.opts = opts;
    this.ws = null;
    this.audioCtx = null;
    this.workletNode = null;
    this.sourceNode = null;
    this.mediaStream = null;
    this.nextPlayTime = 0;
    this.activeSources = new Set();
    this._connected = false;
    this._closed = false;
    this._readyFired = false;
    // Mic input gate — host toggles via setInputEnabled(false) while
    // TTS is playing so we don't fire phantom speech_started events
    // on our own audio.
    this._inputEnabled = true;
    // Tracks function_call ids we've already executed so the
    // response.output_item.done fallback doesn't double-fire.
    this._handledCallIds = new Set();
  }

  /** Pause / resume mic chunks WITHOUT closing the WebSocket. The
   *  worklet keeps capturing but stops posting until re-enabled.
   *  We also drop the server-side buffered audio so the next
   *  resume starts on a fresh utterance, not a stale tail. */
  setInputEnabled(enabled) {
    const next = !!enabled;
    if (next === this._inputEnabled) return;
    this._inputEnabled = next;
    if (!next) {
      // Discard whatever the server has buffered + cancel any
      // in-flight response (defensive — there shouldn't be one
      // in transcription-only mode).
      this._send({ type: 'input_audio_buffer.clear' });
    }
  }

  /** Execute a function call via the server endpoint, then feed
   *  the output back to the model + trigger its next response so
   *  it speaks an acknowledgement. Errors are reported back as
   *  the function output so the model surfaces them to the user
   *  instead of stalling.  */
  async _handleToolCall(name, argsJson, callId) {
    if (this._handledCallIds.has(callId)) return;
    this._handledCallIds.add(callId);
    this.opts.onToolCallStart?.(name, argsJson, callId);

    let outputText = '';
    try {
      const res = await fetch('/api/realtime/tool-call', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name,
          arguments: argsJson,
          chatId: this.opts.chatId || undefined,
        }),
      });
      const j = await res.json().catch(() => ({}));
      outputText = String(j.output || j.error || '(no output)');
    } catch (err) {
      outputText = `Tool error: ${(err && err.message) || 'network'}`;
    }

    this.opts.onToolCallDone?.(name, outputText, callId);

    // Hand the result back to the model as a function_call_output
    // conversation item, then ask for the next response. The model
    // sees the tool output + speaks a follow-up.
    this._send({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: outputText,
      },
    });
    this._send({ type: 'response.create' });
  }

  /** Probe the proxy: 200 means we have an API key + tool catalog;
   *  503 means fall back. Returns `{ available, tools, instructions }`
   *  so the caller can pre-load the tool definitions before opening
   *  the WS (we include them in the session.update event). */
  static async probe() {
    try {
      const res = await fetch('/api/realtime', { method: 'GET', credentials: 'same-origin' });
      if (res.status === 200) {
        const j = await res.json().catch(() => ({}));
        return {
          available: !!j.available,
          tools: Array.isArray(j.tools) ? j.tools : [],
          instructions: typeof j.instructions === 'string' ? j.instructions : '',
        };
      }
      return { available: false, tools: [], instructions: '' };
    } catch { return { available: false, tools: [], instructions: '' }; }
  }

  /** Backwards-compat shim — old callers used probeAvailable() and
   *  only cared about the boolean. Returns just the availability. */
  static async probeAvailable() {
    const p = await RealtimeClient.probe();
    return p.available;
  }

  async connect() {
    if (this._connected) return;

    // 1. Open the WebSocket to our proxy. The cookie travels with
    // the upgrade so the server can verify the user is authed.
    const wsUrl = new URL('/api/realtime', location.href);
    wsUrl.protocol = wsUrl.protocol === 'https:' ? 'wss:' : 'ws:';
    this.ws = new WebSocket(wsUrl.toString());

    // Bounded WS-open promise. Without the timeout a hung handshake
    // (TLS issues, upstream down, Cloudflare cold proxy) would leave
    // the chat stuck on "Connecting…" forever with no fallback.
    await new Promise((resolve, reject) => {
      let settled = false;
      const settle = (fn) => { if (settled) return; settled = true; fn(); };
      const onOpen = () => settle(() => {
        this.ws.removeEventListener('error', onError);
        clearTimeout(t);
        resolve();
      });
      const onError = () => settle(() => {
        this.ws.removeEventListener('open', onOpen);
        clearTimeout(t);
        reject(new Error('ws-open-error'));
      });
      const t = setTimeout(() => settle(() => {
        this.ws.removeEventListener('open', onOpen);
        this.ws.removeEventListener('error', onError);
        try { this.ws.close(); } catch {}
        reject(new Error('ws-open-timeout'));
      }), 5000);
      this.ws.addEventListener('open', onOpen, { once: true });
      this.ws.addEventListener('error', onError, { once: true });
    });
    this.ws.addEventListener('message', (ev) => this._onServerEvent(ev.data));
    this.ws.addEventListener('close', () => {
      this._closed = true;
      this.opts.onClose?.();
    });

    // 2. Configure the session as a STREAMING-WHISPER service:
    // the model runs server-side VAD + audio transcription, but
    // create_response=false means it never auto-generates an
    // answer. We surface the user transcript via onUserTranscript;
    // the host (live-chat) POSTs it to /api/chat (Claude) so the
    // SAME brain answers voice AND typed turns. TTS output goes
    // through /api/tts (Shimmer) on the host side, not through
    // Realtime — Realtime's audio_output stays unused.
    if (this.opts.transcriptionOnly !== false) {
      this._send({
        type: 'session.update',
        session: {
          modalities: ['text'],
          input_audio_format: 'pcm16',
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.35,
            prefix_padding_ms: 300,
            silence_duration_ms: 650,
            create_response: false,
          },
          tool_choice: 'none',
        },
      });
    } else {
      // Legacy full-realtime mode (kept behind transcriptionOnly:false
      // for future experimentation). Realtime owns its own brain +
      // voice. Tools still execute through /api/realtime/tool-call.
      const tools = Array.isArray(this.opts.tools) ? this.opts.tools : [];
      this._send({
        type: 'session.update',
        session: {
          modalities: ['audio', 'text'],
          instructions: this.opts.instructions || DEFAULT_INSTRUCTIONS,
          voice: this.opts.voice || 'shimmer',
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          input_audio_transcription: { model: 'whisper-1' },
          turn_detection: {
            type: 'server_vad',
            threshold: 0.35,
            prefix_padding_ms: 300,
            silence_duration_ms: 650,
            create_response: true,
          },
          tools,
          tool_choice: tools.length > 0 ? 'auto' : 'none',
        },
      });
    }

    // Pre-populate the realtime conversation with the last few
    // turns of THIS chat so the model has context (e.g. user
    // typed something, now switches to voice; or reopens a chat
    // mid-session). We cap at ~10 turns to keep the input-token
    // bill bounded — Realtime input billing is per-token even for
    // text items. Order matters: oldest first.
    const seed = Array.isArray(this.opts.priorMessages) ? this.opts.priorMessages : [];
    const tail = seed.slice(-10);
    for (const m of tail) {
      if (!m || !m.content) continue;
      const role = m.role === 'assistant' ? 'assistant' : 'user';
      // Past USER turns use input_text; past ASSISTANT turns use
      // text. (OpenAI Realtime distinguishes the two content-part
      // discriminators.) We also strip markdown noise so the model
      // isn't biased toward formatting we know it shouldn't emit.
      const text = stripForSeed(String(m.content));
      if (!text) continue;
      this._send({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role,
          content: [{
            type: role === 'user' ? 'input_text' : 'text',
            text,
          }],
        },
      });
    }

    // 3. Set up the audio pipeline. AudioContext at 24 kHz matches
    // the Realtime API's PCM16 format so output playback is sample-
    // accurate without browser resampling. iOS may ignore the
    // requested rate; the worklet handles either case.
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.audioCtx = new Ctx({ sampleRate: 24000 });
    if (this.audioCtx.state === 'suspended') {
      try { await this.audioCtx.resume(); } catch { /* iOS gesture rule */ }
    }
    this.nextPlayTime = this.audioCtx.currentTime;

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    await this.audioCtx.audioWorklet.addModule('/realtime-pcm16-worklet.js');
    this.workletNode = new AudioWorkletNode(this.audioCtx, 'pcm16-capture', {
      processorOptions: { targetSampleRate: 24000 },
    });
    this.sourceNode = this.audioCtx.createMediaStreamSource(this.mediaStream);
    this.sourceNode.connect(this.workletNode);
    // Worklet must be connected somewhere for process() to be
    // called. Route through a gain set to 0 so the mic doesn't echo
    // out the speakers — we ONLY want the worklet's postMessage.
    const sink = this.audioCtx.createGain();
    sink.gain.value = 0;
    this.workletNode.connect(sink);
    sink.connect(this.audioCtx.destination);

    this.workletNode.port.onmessage = (ev) => {
      if (this._closed) return;
      // Gate by inputEnabled — used to pause the mic while Shimmer
      // TTS is playing (without dropping the WS connection). Echo
      // cancellation in the browser is imperfect; pausing input
      // during playback prevents the speech_started VAD from
      // firing on our own TTS audio.
      if (this._inputEnabled === false) return;
      this._send({ type: 'input_audio_buffer.append', audio: arrayBufferToBase64(ev.data) });
    };

    this._connected = true;

    // Watchdog: if session.created / session.updated doesn't arrive
    // within 6 s of the WS open, something's wrong upstream
    // (auth rejected, model overloaded, etc.). Fire onError so the
    // host can fall back to Whisper+TTS instead of staring at
    // "Connecting…" forever.
    setTimeout(() => {
      if (this._closed) return;
      if (!this._readyFired) {
        this.opts.onError?.(new Error('realtime-session-not-ready'));
        try { this.ws?.close(1000, 'session-watchdog'); } catch {}
      }
    }, 6000);
  }

  /** Hard-cancel any in-flight assistant audio + tell the server to
   *  drop the response. Used both for the user-tapped-mic interrupt
   *  case and the server-VAD detected-user-speech case. */
  cancelResponse() {
    this._stopAllPlayback();
    this._send({ type: 'response.cancel' });
  }

  close() {
    this._closed = true;
    try { this.ws?.close(1000, 'client-close'); } catch {}
    try { this.workletNode?.disconnect(); } catch {}
    try { this.sourceNode?.disconnect(); } catch {}
    try { this.mediaStream?.getTracks().forEach(t => t.stop()); } catch {}
    try { this.audioCtx?.close(); } catch {}
    this._stopAllPlayback();
    this.ws = null;
    this.workletNode = null;
    this.sourceNode = null;
    this.mediaStream = null;
    this.audioCtx = null;
    this._connected = false;
  }

  _send(event) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(event));
    }
  }

  _onServerEvent(data) {
    let evt;
    try { evt = JSON.parse(data); } catch { return; }

    switch (evt.type) {
      case 'session.created':
      case 'session.updated':
        // First time the session is configured + ready for audio.
        // Surface a 'ready' signal so the UI can flip the placeholder
        // from "connecting" → "listening".
        if (!this._readyFired) {
          this._readyFired = true;
          this.opts.onReady?.();
        }
        break;

      case 'input_audio_buffer.speech_started':
        // Barge-in: user started speaking while assistant audio was
        // playing. Kill the playback queue + cancel the in-flight
        // response so the model stops generating.
        this._stopAllPlayback();
        this._send({ type: 'response.cancel' });
        this.opts.onSpeechStart?.();
        break;

      case 'input_audio_buffer.speech_stopped':
        this.opts.onSpeechStop?.();
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (evt.transcript) {
          const t = String(evt.transcript).trim();
          // Same hallucination filter as the Whisper-batch path —
          // drop "Untertitel der Amara.org-Community" and similar
          // silence-default outputs before they reach the chat.
          if (!t || t.length < 3) break;
          if (isRealtimeHallucination(t)) break;
          this.opts.onUserTranscript?.(t);
        }
        break;

      case 'response.audio.delta':
        if (evt.delta) this._playAudioChunk(evt.delta);
        break;

      case 'response.audio_transcript.delta':
        if (evt.delta) this.opts.onAssistantTranscriptDelta?.(String(evt.delta));
        break;

      case 'response.audio_transcript.done':
        if (evt.transcript) this.opts.onAssistantTranscriptDone?.(String(evt.transcript));
        break;

      case 'response.function_call_arguments.done': {
        // The model finished a tool call. evt carries call_id, name,
        // and the JSON `arguments` string. Hand off to the host
        // (live-chat) to execute via /api/realtime/tool-call, then
        // we feed the output back as a function_call_output item +
        // trigger the next response so the model speaks an ack.
        const name = String(evt.name || '');
        const callId = String(evt.call_id || '');
        const args = typeof evt.arguments === 'string' ? evt.arguments : '';
        if (name && callId) this._handleToolCall(name, args, callId);
        break;
      }

      case 'response.output_item.done': {
        // Some Realtime API revisions emit the function_call inside
        // response.output_item.done instead of (or in addition to)
        // the explicit response.function_call_arguments.done event.
        // Cover both routes.
        const item = evt.item;
        if (item && item.type === 'function_call' && item.call_id && item.name) {
          // Skip if we already handled it via the dedicated event.
          if (!this._handledCallIds.has(item.call_id)) {
            this._handleToolCall(String(item.name), String(item.arguments || ''), String(item.call_id));
          }
        }
        break;
      }

      case 'response.done': {
        // Belt-and-suspenders: if the per-delta done event didn't
        // fire (older API versions, missing modalities) dig the
        // transcript out of the response payload.
        const out = evt.response?.output?.[0];
        const content = Array.isArray(out?.content) ? out.content : [];
        const audioPart = content.find((c) => c?.type === 'audio');
        const textPart = content.find((c) => c?.type === 'text');
        const text = (audioPart && audioPart.transcript) || (textPart && textPart.text) || '';
        if (text) this.opts.onAssistantTranscriptDone?.(String(text));
        break;
      }

      case 'error':
        this.opts.onError?.(new Error(evt.error?.message || 'realtime-error'));
        break;

      default:
        // Unhandled event types are fine — many are informational
        // (rate.limits.updated, conversation.item.created, …).
        break;
    }
  }

  _playAudioChunk(base64) {
    if (!this.audioCtx) return;
    const bytes = base64ToUint8Array(base64);
    const int16 = new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;
    const buffer = this.audioCtx.createBuffer(1, float32.length, 24000);
    buffer.copyToChannel(float32, 0);
    const source = this.audioCtx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.audioCtx.destination);
    const startTime = Math.max(this.nextPlayTime, this.audioCtx.currentTime);
    source.start(startTime);
    this.nextPlayTime = startTime + buffer.duration;
    this.activeSources.add(source);
    source.onended = () => { this.activeSources.delete(source); };
  }

  _stopAllPlayback() {
    for (const src of this.activeSources) {
      try { src.stop(); } catch {}
    }
    this.activeSources.clear();
    if (this.audioCtx) this.nextPlayTime = this.audioCtx.currentTime;
  }
}

/** Strip markdown noise from a chat message before seeding it into
 *  the Realtime conversation. The model bills by token even on text
 *  context items, and rendering hints (backticks, code blocks, link
 *  URLs) are wasted tokens for a voice context. Cap each message at
 *  800 chars so a long bullet list doesn't dominate the seed budget. */
function stripForSeed(s) {
  return String(s)
    .replace(/```[\s\S]*?```/g, ' (code block) ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\|/g, ' ')
    .replace(/-{3,}/g, '')
    .replace(/\[\[[^\]]+\]\]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 800);
}

const DEFAULT_INSTRUCTIONS = `You are eOS, a voice assistant for an Eraneos consultant. Reply CONVERSATIONALLY:
- 1-3 sentences for simple questions, up to ~60 words for substantive ones.
- Plain prose only. No markdown, no headers, no bullets, no numbered lists.
- Use contractions and a natural spoken rhythm.
- Spell out small numbers (≤ 12). Skip URLs and code in speech.
- Don't pre-acknowledge with "Great question" — just answer.
- One clarifying question max, conversationally.`;

/* ── Whisper hallucination filter ─────────────────────────────────
 * OpenAI's whisper-1 "fills in" specific phrases when given silence
 * or near-silence — the model's captioning training set bleeds
 * through. Drop these BEFORE they reach the chat so the assistant
 * doesn't get bogus user turns (the user-reported "Untertitel der
 * Amara.org-Community" being the canonical example). */
const REALTIME_HALLUCINATIONS = [
  /^untertitel.*amara/i,
  /^subtitles?\b.*amara/i,
  /^subtitles?\b.*by\b/i,
  /^untertitel(ung)?\b/i,
  /^thanks?\s+for\s+watching/i,
  /^thank\s+you\s+for\s+watching/i,
  /^(please\s+)?subscribe\b/i,
  /^ご視聴ありがとうございました/,
  /^字幕/,
  /^\s*\[?\s*(music|applause|laughter|silence)\s*\]?\s*$/i,
  /^♪+/,
  /^[\.…\-—\s]+$/,
];
function isRealtimeHallucination(s) {
  return REALTIME_HALLUCINATIONS.some(re => re.test(s));
}

/* ── base64 helpers ─ avoid loops via Uint8Array TextDecoder
 *    tricks since we deal with PCM bytes that may contain any value. */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  // Chunked btoa to avoid call-stack issues on large buffers.
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToUint8Array(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
